import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { isRenderableImageSrc } from "@/utils/imageSrc";
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/**
 * An `<img>` that hides itself when the source can't render.
 *
 * Broken images were showing up across index cards, banners and featured
 * slots: nothing checked `onError`, so a dead URL (or the `"/"` that
 * `assetUrl("")` produces) painted the browser's broken-image icon. This
 * renders `fallback` — nothing by default — for both cases: a source that
 * fails {@link isRenderableImageSrc} up front, and one that errors at load
 * time. Only the second case is a *failure* a caller can report via
 * `onBroken`; the first means no image was chosen at all.
 */
export default function SafeImage({
  src,
  alt,
  fallback = null,
  sx,
  className,
  width,
  height,
  loading = "lazy",
  onBroken
}) {
  // Trim once and use the trimmed value everywhere: the predicate
  // trims, so an untrimmed src would pass the check and then flash as
  // broken when the browser tried to load it.
  const trimmed = typeof src === "string" ? src.trim() : "";
  const usable = isRenderableImageSrc(trimmed);
  const [errored, setErrored] = useState(false);

  // A new src deserves a fresh attempt — otherwise one bad URL would
  // permanently blank the slot after a re-render with a good one.
  useEffect(() => {
    setErrored(false);
  }, [src]);
  if (!usable || errored) return /*#__PURE__*/_jsx(_Fragment, {
    children: fallback
  });
  return /*#__PURE__*/_jsx(Box, {
    component: "img",
    src: trimmed,
    alt: alt,
    className: className,
    width: width,
    height: height,
    loading: loading,
    onError: () => {
      setErrored(true);
      onBroken?.(trimmed);
    },
    sx: sx
  });
}